import { Directory, Filesystem } from "@capacitor/filesystem"
import { Share } from "@capacitor/share"
import { shareURL } from "@telegram-apps/sdk"
import { copyToClipboard } from "./clipboard"
import { detectPlatform } from "./detect"

export interface ShareLinkParams {
	url: string
}

export interface ShareLinkResult {
	/** True если использовали fallback на буфер обмена — caller может показать toast. */
	copiedFallback: boolean
}

/**
 * Кроссплатформенный шеринг ссылки:
 * - Telegram MA: shareURL (нативный диалог Telegram)
 * - iOS/Android (Capacitor): Share.share()
 * - Browser: navigator.share, fallback на copyToClipboard
 */
export async function shareLink({
	url,
}: ShareLinkParams): Promise<ShareLinkResult> {
	const platform = detectPlatform()

	if (platform === "telegram") {
		shareURL.ifAvailable(url)
		return { copiedFallback: false }
	}

	if (platform === "ios" || platform === "android") {
		try {
			await Share.share({ url })
		} catch (error) {
			if (!(error instanceof Error) || error.message !== "Share canceled") {
				throw error
			}
		}
		return { copiedFallback: false }
	}

	if (typeof navigator !== "undefined" && "share" in navigator) {
		try {
			await navigator.share({ url })
			return { copiedFallback: false }
		} catch {
			// user cancelled or unsupported — fall through to copy
		}
	}

	const copied = await copyToClipboard(url)
	return { copiedFallback: copied }
}

export interface ShareFileParams {
	blob: Blob
	/** Имя временного файла в Cache (с расширением). */
	fileName: string
	title?: string
	text?: string
	url?: string
}

function blobToBase64(blob: Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader()
		reader.onload = () => {
			const result = reader.result as string
			resolve(result.slice(result.indexOf(",") + 1))
		}
		reader.onerror = () => reject(reader.error)
		reader.readAsDataURL(blob)
	})
}

/**
 * Нативный шеринг файла (iOS/Android): кладёт blob во временный файл в Cache и
 * отдаёт его системному share-диалогу как вложение. `@capacitor/share` принимает
 * только локальный file:// путь, поэтому ассет всегда пишем в Cache (Android из
 * коробки шарит только cache-папку) и подчищаем после.
 */
export async function shareFileNative({
	blob,
	fileName,
	...content
}: ShareFileParams): Promise<void> {
	const base64 = await blobToBase64(blob)
	const written = await Filesystem.writeFile({
		path: fileName,
		data: base64,
		directory: Directory.Cache,
	})

	try {
		await Share.share({ ...content, files: [written.uri] })
	} finally {
		Filesystem.deleteFile({
			path: fileName,
			directory: Directory.Cache,
		}).catch(() => {})
	}
}
